import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { CANDIDATE_STATUS } from "src/_config";

interface InterviewResponseParams {
  responseType: string; // "accept" | "decline"
  screeningId: string;
  userId?: string;
}

// Store outside component to persist across remounts (React Strict Mode)
const processedRequests = new Set<string>();

const InterviewResponseMain = () => {
  const { responseType, screeningId, userId } = useParams<InterviewResponseParams>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasProcessed, setHasProcessed] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const hasCalledRef = useRef(false);

  const screeningApi = useScreeningApi();
  const toast = useToaster();
  const history = useHistory();

  const handleInterviewResponse = () => {
    const requestKey = `${screeningId}-${responseType}`;

    // console.log(">>>>>>>>>>>>>> handleInterviewResponse called", {
    //   requestKey,
    //   alreadyProcessed: processedRequests.has(requestKey),
    //   hasCalledRef: hasCalledRef.current,
    //   isProcessing,
    //   hasProcessed,
    // });

    // Check if this specific request has already been processed
    if (processedRequests.has(requestKey)) {
     // console.log(">>>>>>>>>>>>>> Request already processed globally, returning");
      return;
    }

    if (hasCalledRef.current) {
      //console.log(">>>>>>>>>>>>>> Ref check: Already called, returning");
      return;
    }

    if (isProcessing || hasProcessed) {
      //console.log(">>>>>>>>>>>>>> State check: Already processing, returning");
      return;
    }

    //console.log(">>>>>>>>>>>>>> Setting hasCalledRef to true and marking request as processed");
    hasCalledRef.current = true;
    processedRequests.add(requestKey);

    const isAccept = responseType === "accept";
    const isReject = responseType === "decline";

    if (!isAccept && !isReject) {
      setErrorMessage("Invalid interview response type. Please use a valid link.");
      return;
    }

    setIsProcessing(true);
    setHasProcessed(true);

    const requestData = {
      user_id: 0,
      screening_id: screeningId,
      title: isAccept ? "Interview Accepted" : "Interview Declined",
      description: isAccept
        ? "Candidate has accepted the interview invitation"
        : "Candidate has declined the interview invitation",
      application_type: 1, // TODO: Confirm application type for interview response
      status: isAccept ? 11 : 12, // TODO: Confirm status codes for interview accept/decline
    };

    //console.log(">>>>>>>>>>>>>>", screeningId);

    screeningApi.callAddFeedback(
      requestData,
      (message: string, response: any) => {
        setIsProcessing(false);
        setIsSuccess(true);
        toast.success(
          isAccept
            ? "Thank you! Your interview acceptance has been recorded."
            : "Your interview response has been recorded."
        );
      },
      (error: string) => {
        setIsProcessing(false);
        setErrorMessage(
          isAccept
            ? "Failed to record your interview acceptance. Please try again or contact support."
            : "Failed to record your interview response. Please try again or contact support."
        );
        toast.error(error);
      }
    );

    screeningApi.callUpdateStatus(
      {
        screening_id: screeningId,
        status: isAccept ? CANDIDATE_STATUS.CANDIDATE_ACCEPT_VIA_MAIL : CANDIDATE_STATUS.CANDIDATE_REJECT_VIA_MAIL,
      },
      (message: string, resp: any) => {
        setIsProcessing(false);
        setIsSuccess(true);
        toast.success(
          isAccept
            ? "Thank you! Your interview acceptance has been recorded."
            : "Your interview response has been recorded."
        );
      },
      (error: string) => {
        setIsProcessing(false);
        setErrorMessage(
          isAccept
            ? "Failed to record your interview acceptance. Please try again or contact support."
            : "Failed to record your interview response. Please try again or contact support."
        );
        toast.error(error);
      }
    );
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const isAccept = responseType === "accept";
  const isReject = responseType === "decline";

  useEffect(() => {
    // console.log(">>>>>>>>>>>>>> useEffect called", {
    //   screeningId,
    //   responseType,
    //   hasProcessed,
    //   hasCalledRef: hasCalledRef.current,
    // });

    // Use ref to ensure it only runs once, even in Strict Mode
    if (hasCalledRef.current) {
     // console.log(">>>>>>>>>>>>>> Already called, skipping");
      return;
    }

    if (screeningId && responseType) {
      //console.log(">>>>>>>>>>>>>> Calling handleInterviewResponse");
      handleInterviewResponse();
    }

    // Cleanup function (won't help with double call but good practice)
    return () => {
     // console.log(">>>>>>>>>>>>>> useEffect cleanup");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="offer-response-container">
      <div className="offer-response-card">
        <div className="offer-response-header">
          <div className="mb-3">
            <img src="/images/dr-logo.png" alt="Dr. Job Pro" />
          </div>
          <h1 className="offer-response-title">
            {isAccept ? "Accept Interview Invitation" : isReject ? "Decline Interview Invitation" : "Invalid Request"}
          </h1>
        </div>

        <div className="offer-response-content">
          {isProcessing && (
            <div className="processing-state">
              <div className="loading-spinner"></div>
              <h2>Processing your response...</h2>
              <p>Please wait while we record your decision.</p>
            </div>
          )}

          {!isProcessing && isSuccess && (
            <div className="success-state">
              <div className="success-icon">
                <i className="fa fa-check-circle"></i>
              </div>
              <h2>{isAccept ? "Interview Accepted" : "Response Recorded"}</h2>
              <p>
                {isAccept
                  ? "You have successfully accepted the interview invitation. The hiring team has been notified and will share the details."
                  : "Thank you for your response. We appreciate the time you took to consider the interview invitation."}
              </p>
              <button className="btn-preview" onClick={handleGoHome}>
                Visit Dr. Job Pro
              </button>
            </div>
          )}

          {!isProcessing && !isSuccess && errorMessage && (
            <div className="error-state">
              <div className="error-icon">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <h2>Something went wrong</h2>
              <p>{errorMessage}</p>
              <div className="error-actions">
                <button className="btn-secondary" onClick={() => window.location.reload()}>
                  Try Again
                </button>
                <button className="btn-primary" onClick={handleGoHome}>
                  Go to Dr. Job Pro
                </button>
              </div>
            </div>
          )}

          {!isAccept && !isReject && !isProcessing && (
            <div className="error-state">
              <div className="error-icon">
                <i className="fa fa-question-circle"></i>
              </div>
              <h2>Invalid Link</h2>
              <p>This link appears to be invalid or corrupted. Please check the URL or contact the hiring team.</p>
              <button className="btn-preview" onClick={handleGoHome}>
                Go to Dr. Job Pro
              </button>
            </div>
          )}
        </div>

        <div className="offer-response-footer">
          <p>
            <i className="fa fa-shield"></i>
            This response is securely processed and recorded.
          </p>
          <p>
            Need help? Contact us at <a href="mailto:support@drjobpro.com">support@drjobpro.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default InterviewResponseMain;
